import React, { useCallback, useMemo, memo } from 'react'
import SystemMenu from '../../Library/ProfilePlaylistMenuOptions/SystemMenu'
import PlaylistSeeEntireLink from '../../Library/PlaylistSeeEntireLink/index'
import type { EditToolbarLabels, CarouselItem }
  from '../CommunityProfileCarousels/index'
import PlaylistRow from './PlaylistRow'
import type { PlaylistRowContextValue } from './PlaylistRowContext'
import RowEditDoneButton from './RowEditDoneButton'

/**
 * Variant: Currently Watching / Watch History.
 *
 * These two rows receive their items as a flat, already-enriched array
 * from external hydration paths (`useInProgressEnrichment`,
 * `useWatchHistoryData`). They do NOT call `useRowHydration` — the
 * upstream hook owns hydration. This variant is purely presentational:
 * frame + header + body decision tree.
 *
 * No statusTag, no shareButton (read-only surfaces — legacy parity).
 *
 * Edit menu / Done toggle wired when `onAction` and `isEditing` are
 * provided — matches `SystemPlaylistShellRow` behavior so Currently
 * Watching + Watch History keep their owner-side edit affordance
 * (parity with the legacy `SystemPlaylistRow.tsx` before Phase 4).
 */

export type SystemPlaylistAction = (
  playlistId: string,
  action: string,
  playlistType?: string,
  playlistName?: string,
) => void

export interface SystemPlaylistExternalRowProps {
  playlistId: string
  label: string
  items: readonly CarouselItem[]
  totalCount: number
  totalDuration: string | null
  isHydrating?: boolean
  seeMoreLabel: string
  showLessLabel?: string
  seeEntireTemplate?: string
  viewAllRoute?: string | null
  customStats?: React.ReactNode
  /** Optional remove handler — Currently Watching uses this; Watch History does not. */
  onRemoveItem?: (item: CarouselItem) => void
  editToolbarLabels?: EditToolbarLabels
  /** Edit-mode toggle — when true, header swaps overflow menu for a Done pill. */
  isEditing?: boolean
  doneLabel?: string
  onDoneEditing?: () => void
  /** Menu-action dispatcher — `action='edit'` flips the parent's editing state. */
  onAction?: SystemPlaylistAction
  /**
   * Skeleton-count fallback for the no-shell variants. When `isHydrating`
   * is true and `items` is empty, `PlaylistRow.Body` falls back to this
   * count for the loading skeleton (shell rows would use the shell's
   * `expectedItemCount` instead). Default 4 to match other carousel rows.
   */
  skeletonCount?: number
  /**
   * Opt in to the series-label corner-badge placeholder in the skeleton
   * branch.  Default `false`; Currently Watching passes `true` because
   * its tiles are series-pivoted.  Watch History (the other consumer of
   * this row) leaves it off.
   */
  showSkeletonSeriesLabel?: boolean
  /** Optional node rendered when not hydrating and `items` is empty. */
  emptyState?: React.ReactNode
}

function SystemPlaylistExternalRowImpl ({
  playlistId,
  label,
  items,
  totalCount,
  totalDuration,
  isHydrating = false,
  seeMoreLabel,
  showLessLabel,
  seeEntireTemplate,
  viewAllRoute,
  customStats,
  onRemoveItem,
  editToolbarLabels,
  isEditing = false,
  doneLabel,
  onDoneEditing,
  onAction,
  skeletonCount = 4,
  showSkeletonSeriesLabel = false,
  emptyState,
}: SystemPlaylistExternalRowProps) {
  const count = totalCount || items.length

  const ctx = useMemo<PlaylistRowContextValue>(() => ({
    playlistId,
    playlistName: label,
    items,
    expectedItemCount: totalCount,
    totalDuration,
    isHydrating,
    isEditing,
  }), [playlistId, label, items, totalCount, totalDuration, isHydrating, isEditing])

  const seeEntireLabel = seeEntireTemplate && count > 0
    ? seeEntireTemplate.replace('{count}', String(count))
    : ''

  // Stable SystemMenu edit handler — see SystemPlaylistShellRow.tsx for
  // the rationale (inline arrow defeated `<SystemMenu>`'s `React.memo`).
  const handleSystemMenuEdit = useCallback((id: string, type?: string, name?: string) => {
    if (onAction) onAction(id, 'edit', type, name)
  }, [onAction])

  // nameAction: Done button while editing, SystemMenu (bare edit trigger)
  // otherwise. Mirrors `SystemPlaylistShellRow.tsx:141-162` so Currently
  // Watching / Watch History match Watch Later / Favorites visually.
  //
  // Memoised so `<PlaylistRow.Header>` forwards a stable ReactElement ref
  // to the React.memo'd `CommunityPlaylistRowHeader` — see
  // `MyPlaylistRow.tsx` for the canonical rationale.
  const nameAction = useMemo<React.ReactNode>(() => {
    if (isEditing && onDoneEditing && doneLabel) {
      return <RowEditDoneButton onClick={onDoneEditing} label={doneLabel} />
    }
    if (onAction) {
      return (
        <SystemMenu
          playlistId={playlistId}
          onEdit={handleSystemMenuEdit}
        />
      )
    }
    return null
  }, [isEditing, onDoneEditing, doneLabel, onAction, playlistId, handleSystemMenuEdit])

  return (
    <PlaylistRow.Provider value={ctx}>
      <PlaylistRow.Frame
        className="profile-my-playlists__playlist-row"
        id={`system-playlist-${playlistId}`}
      >
        <PlaylistRow.Header nameAction={nameAction} customStats={customStats} />
        <PlaylistRow.Body
          seeMoreLabel={seeMoreLabel}
          showLessLabel={showLessLabel}
          hideToggle
          editToolbarLabels={editToolbarLabels}
          onRemoveItem={onRemoveItem}
          skeletonCount={skeletonCount}
          showSkeletonSeriesLabel={showSkeletonSeriesLabel}
          emptyState={emptyState}
        />
        {/*
          Gate the "See entire" link on the SHELL-known `count` (= totalCount
          || items.length), not on `items.length`. During the skeleton window
          the row mounts the link alongside the skeleton tiles so the layout
          doesn't shift on hydration.
        */}
        {count > 0 && viewAllRoute && seeEntireTemplate ? (
          <PlaylistSeeEntireLink
            to={viewAllRoute}
            videoCount={count}
            label={seeEntireLabel}
          />
        ) : null}
      </PlaylistRow.Frame>
    </PlaylistRow.Provider>
  )
}

const SystemPlaylistExternalRow = memo(SystemPlaylistExternalRowImpl)
SystemPlaylistExternalRow.displayName = 'SystemPlaylistExternalRow'
export default SystemPlaylistExternalRow
